import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Moon, Sun, LogOut, User } from 'lucide-react';
import { Badge } from './ui/badge';
import { ScrollToTop } from './ScrollToTop';
import { userRoleBadgeLabel } from '../lib/accountRoles';
import { BrandLogo } from './BrandLogo';
import { useIsMobile } from './ui/use-mobile';

interface LayoutProps {
  children: React.ReactNode;
  noPadding?: boolean;
}

export const Layout = React.memo(function Layout({ children, noPadding = false }: LayoutProps) {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const isMobile = useIsMobile();

  if (!user) return <>{children}</>;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto max-w-full px-3 sm:px-4 flex h-14 sm:h-16 items-center justify-between gap-2">
          {/* Logo */}
          <BrandLogo size="sm" showName={!isMobile} />

          {/* User Info & Actions */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
              {!isMobile && <User className="w-4 h-4 shrink-0" />}

              <Badge
                variant={user.role === 'admin' ? 'default' : 'secondary'}
                className="max-w-[10.5rem] sm:max-w-none truncate text-xs sm:text-sm"
              >
                {userRoleBadgeLabel(user.role, user.parentAccountId, user.distributorLevel)}
              </Badge>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={noPadding ? "max-w-full overflow-x-hidden" : "container mx-auto max-w-full overflow-x-hidden px-3 sm:px-4 py-4 sm:py-6"}>
        {children}
      </main>

      {/* Scroll to Top Button - Mobile Only */}
      <ScrollToTop />
    </div>
  );
});